"use client";

import React, { useEffect, useState, useTransition } from "react";
import { X, ShoppingBag, Trash2, Minus, Plus, ArrowRight } from "lucide-react";
import {
  retrieveCart,
  updateLineItem,
  deleteLineItem,
} from "@/app/actions/cart";
import PurchaseModalPopup from "@/app/components/PurchaseModalPopup";
import AuthModal from "./AuthModal";
import { proceedToCheckoutEvent } from "../utils/gtm-events/contact";
import { motion, AnimatePresence } from "framer-motion";

type ServerCart = {
  id: number;
  attributes: {
    currency?: string;
    currencySymbol?: string;
    totalAmount?: number;
    totalItems?: number;
    lineItems?: { data: any[] };
    cartSessionId?: string;
  };
};

export default function MiniCart({ onClose }: { onClose(): void }) {
  const [serverCart, setServerCart] = useState<ServerCart | null>(null);
  const [isPending, startTransition] = useTransition();
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [cartLoading, setCartLoading] = useState(true);

  const loadCart = async () => {
    setCartLoading(true);
    const res = await retrieveCart();
    setServerCart(res?.cart ?? res ?? null);
    setCartLoading(false);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    loadCart();
    return () => { document.body.style.overflow = "auto"; };
  }, []);

  useEffect(() => {
    fetch("/api/me", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setIsLoggedIn(!!data.isLoggedIn))
      .catch(() => setIsLoggedIn(false))
      .finally(() => setAuthChecked(true));
  }, []);

  const lineItems = serverCart?.attributes?.lineItems?.data ?? [];
  const currencySymbol = serverCart?.attributes?.currencySymbol ?? "₹";
  const currency = serverCart?.attributes?.currency || "INR";
  const subtotal = serverCart?.attributes?.totalAmount ?? 0;

  const fmt = (v: number) => Intl.NumberFormat("en-IN").format(v);

  const handleCheckoutClick = () => {
    if (!authChecked) return;
    proceedToCheckoutEvent({
      pageLocation: window.location.pathname,
      cartValue: subtotal,
      numItems: lineItems.length,
      currency,
    });
    if (isLoggedIn) setIsPurchaseOpen(true);
    else setShowAuthModal(true);
  };

  const handleQtyUpdate = (lineId: number, qty: number) => {
    if (qty < 1) return;
    startTransition(async () => {
      setLoadingId(lineId);
      await updateLineItem({ lineId, quantity: qty });
      await loadCart();
      setLoadingId(null);
    });
  };

  const handleDelete = (lineId: number) => {
    startTransition(async () => {
      setLoadingId(lineId);
      await deleteLineItem(lineId);
      setConfirmDeleteId(null);
      await loadCart();
      setLoadingId(null);
    });
  };

  const getVariantSize = (item: any) => {
    const variantTitle = item.attributes.variant?.data?.attributes?.title;
    if (!variantTitle) return null;
    const sizeMatch = variantTitle.match(/(\d+(?:\.\d+)?\s*(?:kg|g|ml|l|oz|lb)?)/i);
    return sizeMatch ? sizeMatch[1].trim() : variantTitle;
  };

  return (
    <div className="fixed inset-0 z-[100] flex justify-end  ">
      {/* Sleek Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={() => { if (!isPurchaseOpen) onClose(); }}
      />

      <motion.aside 
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        className="relative w-full max-w-[440px] bg-white h-full flex flex-col shadow-[0_0_50px_rgba(0,0,0,0.3)] border-l border-neutral-100"
      >
        {/* Top Progress Line - High Visibility Loader */}
        <AnimatePresence>
          {(loadingId !== null || isPending) && (
            <motion.div 
              initial={{ scaleX: 0 }} 
              animate={{ scaleX: 1 }} 
              exit={{ opacity: 0 }}
              className="absolute top-0 left-0 right-0 h-1 bg-black origin-left z-[110]"
              style={{ backgroundColor: "var(--secondary)" }}
            />
          )}
        </AnimatePresence>

        {/* --- HEADER --- */}
        <header className="px-10 pt-16 pb-8 border-b border-neutral-50">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-[10px] font-black uppercase tracking-[0.5em] text-gray-700">Inventory / Selection</p>
              <h2 className="text-3xl font-black uppercase tracking-tighter text-black leading-none">
                Your <span className="italic" style={{ color: "var(--secondary)" }}>Cart</span>
              </h2>
            </div>
            <button
              onClick={onClose}
              className="group p-2 -mr-2 transition-transform hover:rotate-90 duration-300"
            >
              <X size={24} strokeWidth={1} className="text-gray-700 group-hover:text-black" />
            </button>
          </div>
        </header>

        {/* --- LIST AREA --- */}
        <div className="flex-1 overflow-y-auto px-10 py-6 scrollbar-hide bg-neutral-50/30">
          {cartLoading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="w-1.5 h-1.5 rounded-full bg-black animate-ping" />
            </div>
          ) : lineItems.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center opacity-30">
              <ShoppingBag size={40} strokeWidth={1} className="mb-6" />
              <p className="text-[10px] font-black uppercase tracking-[0.4em]">Archive Empty</p>
            </div>
          ) : (
            <div className="space-y-10">
              {lineItems.map((item: any) => {
                const attr = item.attributes;
                const product = attr.product?.data?.attributes;
                const imgSrc = product?.wb_product_images?.data?.[0]?.attributes?.src || product?.images?.data?.[0]?.attributes?.url;
                const isConfirmingDelete = confirmDeleteId === item.id;

                return (
                  <div key={item.id} className="relative group pb-10 border-b border-neutral-100 last:border-0">
                    <div className="flex gap-8">
                      {/* Image Frame */}
                      <div className="relative h-32 w-24 flex-shrink-0 bg-white shadow-sm ring-1 ring-neutral-100 overflow-hidden">
                        <img 
                          src={imgSrc} 
                          alt="Product" 
                          className="h-full w-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-700" 
                        />
                      </div>

                      <div className="flex flex-1 flex-col justify-between py-1">
                        {!isConfirmingDelete ? (
                          <>
                            <div className="space-y-1.5">
                              <div className="flex justify-between items-start">
                                <h3 className="text-[13px] font-black uppercase tracking-tight leading-tight text-black flex-1">
                                  {product?.Title}
                                </h3>
                                <button 
                                  onClick={() => setConfirmDeleteId(item.id)} 
                                  className="text-neutral-300 hover:text-red-500 transition-colors ml-4"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              <p className="text-[9px] font-bold text-gray-700 uppercase tracking-widest italic">
                                Size / {getVariantSize(item) || "STND"}
                              </p>
                            </div>

                            <div className="flex items-end justify-between">
                              {/* Sleek Qty Toggle */}
                              <div className="flex items-center border border-neutral-200">
                                <button 
                                  onClick={() => handleQtyUpdate(item.id, attr.quantity - 1)}
                                  className="w-8 h-8 flex items-center justify-center text-gray-700 hover:text-black hover:bg-neutral-50 transition-colors"
                                >
                                  <Minus size={12} />
                                </button>
                                <span className="w-8 text-center text-[11px] font-black text-black">{attr.quantity}</span>
                                <button 
                                  onClick={() => handleQtyUpdate(item.id, attr.quantity + 1)}
                                  className="w-8 h-8 flex items-center justify-center text-gray-700 hover:text-black hover:bg-neutral-50 transition-colors"
                                >
                                  <Plus size={12} />
                                </button>
                              </div>
                              <p className="text-[14px] font-black tracking-tighter text-black">
                                {currencySymbol}{fmt(attr.unitPrice * attr.quantity)}
                              </p>
                            </div>
                          </>
                        ) : (
                          <motion.div 
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            className="h-full flex flex-col items-center justify-center space-y-4 text-center"
                          >
                            <p className="text-[10px] font-black uppercase tracking-widest text-red-500">Remove Piece?</p>
                            <div className="flex gap-6">
                              <button onClick={() => setConfirmDeleteId(null)} className="text-[10px] font-black uppercase underline decoration-neutral-200">Cancel</button>
                              <button onClick={() => handleDelete(item.id)} className="text-[10px] font-black uppercase text-red-500 underline decoration-red-200">Confirm</button>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* --- FOOTER --- */}
        {lineItems.length > 0 && (
          <div className="p-10 bg-white border-t border-neutral-100 space-y-8">
            <div className="flex justify-between items-baseline">
              <span className="text-[10px] font-black uppercase tracking-[0.5em] text-gray-700">Total Selection</span>
              <span className="text-3xl font-black tracking-tighter text-black">
                {currencySymbol}{fmt(subtotal)}
              </span>
            </div>

            <button
              onClick={handleCheckoutClick}
              disabled={!authChecked}
              className="group relative w-full py-6 bg-black text-white text-[11px] font-black uppercase tracking-[0.4em] overflow-hidden flex items-center justify-center gap-4 transition-all hover:bg-[var(--secondary)] active:scale-[0.98]"
            >
              <span>{authChecked ? "Verify & Checkout" : "System Synchronizing"}</span>
              <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform duration-500" />
            </button>
            
            <p className="text-[8px] font-bold text-neutral-300 uppercase tracking-[0.3em] text-center leading-loose">
              Logistics & Duties calculated during <br /> final archive entry
            </p>
          </div>
        )}

        {/* Brand Progress Line */}
        <div className="h-1.5 w-full flex shrink-0">
          <div className="h-full w-1/3 bg-black" />
          <div className="h-full w-2/3" style={{ backgroundColor: 'var(--secondary)' }} />
        </div>
      </motion.aside>

      {/* Modals - Logics intact */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={async () => {
          setShowAuthModal(false);
          await loadCart();
          setIsPurchaseOpen(true);
        }}
      />

      <PurchaseModalPopup
        isOpen={isPurchaseOpen}
        onClose={() => setIsPurchaseOpen(false)}
        product={{ price: subtotal, currency, currencySymbol }}
        orderItems={lineItems}
        cartSessionId={serverCart?.attributes?.cartSessionId}
        preOrder={false}
        subtotal={subtotal}
      />
    </div>
  );
}